/**
 * Atlas PM · 系统设置标签定义（P4.2 框架）
 *
 * ⭐ 四个标签的「唯一真相源」：
 *    - 标签文案 / 子路由 / 所需权限集中在此
 *    - 容器侧栏、路由 index 重定向都从这里派生，避免两处写死不一致
 *
 * ⚠️ P4.3-P4.6 只需替换对应的占位组件，不要改这里的路由与权限映射。
 */

export interface SettingsTab {
  /** 稳定标识（用于 key） */
  key: string;
  /** 子路由绝对路径 */
  to: string;
  /** 标签文案 */
  label: string;
  /** 标签图标（几何符号，与既有导航风格一致） */
  icon: string;
  /** 访问该标签所需权限点（单一） */
  permission: string;
}

/** 系统设置入口可达性权限：任一满足即认为可进入系统设置 */
export const SETTINGS_ENTRY_PERMISSIONS: readonly string[] = [
  'user:manage',
  'role:manage',
  'dict:manage',
  'audit:view',
  // P8.2：仅有任务模板管理权限的用户也能进入系统设置（否则其看不到「任务模板」菜单）
  'template:manage',
];

/** 标签（顺序即默认跳转优先级） */
export const SETTINGS_TABS: readonly SettingsTab[] = [
  {
    key: 'users',
    to: '/settings/users',
    label: '用户管理',
    icon: '❖',
    permission: 'user:manage',
  },
  {
    key: 'roles',
    to: '/settings/roles',
    label: '权限配置',
    icon: '⊞',
    permission: 'role:manage',
  },
  {
    key: 'project-types',
    to: '/settings/project-types',
    label: '项目类型',
    icon: '▣',
    permission: 'dict:manage',
  },
  {
    key: 'task-categories',
    to: '/settings/task-categories',
    label: '任务形态',
    icon: '◇',
    permission: 'dict:manage',
  },
  {
    key: 'audit',
    to: '/settings/audit',
    label: '操作日志',
    icon: '⛓',
    permission: 'audit:view',
  },
  {
    key: 'task-templates',
    to: '/settings/task-templates',
    label: '任务模板',
    icon: '⧉',
    permission: 'template:manage',
  },
];

/** 当前用户可见的标签（无权限的不渲染） */
export function allowedSettingsTabs(
  hasPermission: (code: string) => boolean,
): SettingsTab[] {
  return SETTINGS_TABS.filter((tab) => hasPermission(tab.permission));
}

/** 默认标签路径：该用户有权限的第一个标签；一个都没有则返回 null */
export function firstAllowedSettingsPath(
  hasPermission: (code: string) => boolean,
): string | null {
  const first = allowedSettingsTabs(hasPermission)[0];
  return first ? first.to : null;
}
